import {
  DEFAULT_WORKFLOW_TEMPLATE,
  flattenWorkflowTemplate,
  type WorkflowTemplate,
} from '@/lib/shipmentWorkflow';

export type EventPhase =
  | 'initial'
  | 'in_transit'
  | 'out_for_delivery'
  | 'damaged'
  | 'return'
  | 'replacement_review'
  | 'replacement_transit'
  | 'delivered';

export type EventState = 'completed' | 'active' | 'pending' | 'damage';

export interface TrackingEvent {
  id: string;
  dayOffset: number;
  label: string;
  description: string;
  location: string;
  phase: EventPhase;
  isDamageEvent?: boolean;
  isReplacementEvent?: boolean;
  timestamp?: string;
  state: EventState;
  proof?: {
    filename: string;
    dataUrl: string;
    uploadedAt: string;
  };
}

export interface TrackingResult {
  trackingNumber: string;
  orderNumber: string;
  storeName: string;
  storeAddress: string;
  shippingWarehouseAddress: string;
  secondaryWarehouseAddress: string;
  customerName: string;
  customerAddress: string;
  orderDate: string;
  numberOfPieces: number;
  totalShipmentWeightLbs: number;
  currentStatus: string;
  currentPhase: EventPhase;
  progressPercent: number;
  events: TrackingEvent[];
  estimatedDelivery: string;
  estimatedDeliveryNote: string;
  isDelivered: boolean;
  isDamaged: boolean;
  hasReplacement: boolean;
}

export interface TrackingRouteStop {
  id: string;
  label: string;
  address: string;
  status: 'completed' | 'current' | 'upcoming';
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateRange(start: Date, end: Date): string {
  return `${formatDate(start)} - ${formatDate(end)}`;
}

function formatDateTime(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function parseDateInput(value: string | Date): Date {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? new Date() : new Date(value);
  }

  if (typeof value !== 'string') {
    return new Date();
  }

  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

function roundUpToNearestMinutes(date: Date, minutes: number): Date {
  const result = new Date(date);
  const ms = 1000 * 60 * minutes;
  result.setTime(Math.ceil(result.getTime() / ms) * ms);
  result.setSeconds(0, 0);
  return result;
}

function compactAddress(address: string | undefined) {
  return (address || '').trim();
}

function phaseLocation(
  phase: EventPhase,
  storeAddress: string,
  shippingWarehouseAddress: string,
  secondaryWarehouseAddress: string,
  customerAddress: string,
): string {
  const store = compactAddress(storeAddress);
  const warehouse1 = compactAddress(shippingWarehouseAddress);
  const warehouse2 = compactAddress(secondaryWarehouseAddress);
  const customer = compactAddress(customerAddress);

  switch (phase) {
    case 'initial':
      return store ? `Store pickup · ${store}` : 'Store pickup';
    case 'in_transit':
      if (warehouse1 && warehouse2) return `Linehaul · ${warehouse1} -> ${warehouse2}`;
      if (warehouse1) return `Transit to warehouse · ${warehouse1}`;
      return 'Transit network';
    case 'out_for_delivery':
      if (warehouse2) return `Final mile from warehouse 2 · ${warehouse2}`;
      if (warehouse1) return `Final mile from warehouse 1 · ${warehouse1}`;
      return customer ? `Out for delivery · ${customer}` : 'Local delivery route';
    case 'damaged':
      if (warehouse2) return `Issue reported near warehouse 2 leg · ${warehouse2}`;
      if (warehouse1) return `Issue reported near warehouse 1 leg · ${warehouse1}`;
      return customer ? `Issue reported near destination · ${customer}` : 'Delivery route';
    case 'return':
      if (warehouse2 && store) return `Return route · ${warehouse2} -> ${store}`;
      if (warehouse1 && store) return `Return route · ${warehouse1} -> ${store}`;
      return 'Return logistics';
    case 'replacement_review':
      if (store) return `Replacement review at store · ${store}`;
      if (warehouse1) return `Replacement review · ${warehouse1}`;
      return 'Replacement review center';
    case 'replacement_transit':
      if (warehouse1 && warehouse2) return `Replacement route · ${warehouse1} -> ${warehouse2}`;
      if (warehouse1) return `Replacement transit · ${warehouse1}`;
      return 'Replacement transit network';
    case 'delivered':
      return customer ? `Delivered · ${customer}` : 'Customer location';
    default:
      return 'Bolt Freight network';
  }
}

function phaseTimeSlot(phase: EventPhase): { hour: number; minute: number } {
  switch (phase) {
    case 'initial':
      return { hour: 9, minute: 0 };
    case 'in_transit':
      return { hour: 12, minute: 30 };
    case 'out_for_delivery':
      return { hour: 8, minute: 30 };
    case 'damaged':
      return { hour: 14, minute: 15 };
    case 'return':
      return { hour: 16, minute: 0 };
    case 'replacement_review':
      return { hour: 10, minute: 30 };
    case 'replacement_transit':
      return { hour: 13, minute: 0 };
    case 'delivered':
      return { hour: 17, minute: 45 };
    default:
      return { hour: 11, minute: 0 };
  }
}

export function computeTracking(
  trackingNumber: string,
  orderNumber: string,
  storeName: string,
  storeAddress: string,
  shippingWarehouseAddress: string,
  secondaryWarehouseAddress: string,
  customerName: string,
  customerAddress: string,
  orderDateStr: string,
  currentDateStr?: string,
  storedTrackingEventsJson?: string,
  workflowTemplate?: WorkflowTemplate,
  productItemsJson?: string,
  totalWeightLbsInput?: number,
): TrackingResult {
  const orderDate = parseDateInput(orderDateStr);
  const today = currentDateStr ? parseDateInput(currentDateStr) : new Date();
  const elapsedMs = today.getTime() - orderDate.getTime();
  const daysSinceOrder = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));

  const template = workflowTemplate || DEFAULT_WORKFLOW_TEMPLATE;
  const { numberOfPieces, totalShipmentWeightLbs } = (() => {
    let pieces = 1;
    let weight = Number(totalWeightLbsInput || 0);

    if (!productItemsJson) {
      return {
        numberOfPieces: pieces,
        totalShipmentWeightLbs: Number.isFinite(weight) ? weight : 0,
      };
    }

    try {
      const parsed = JSON.parse(productItemsJson) as Array<{ qty?: number; weightLbs?: number }>;
      if (!Array.isArray(parsed) || parsed.length === 0) {
        return {
          numberOfPieces: pieces,
          totalShipmentWeightLbs: Number.isFinite(weight) ? weight : 0,
        };
      }

      const qtyTotal = parsed.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0);
      pieces = qtyTotal > 0 ? qtyTotal : parsed.length;

      const computedWeight = parsed.reduce((sum, item) => {
        const qty = Number(item?.qty) || 0;
        const unitWeight = Number(item?.weightLbs) || 0;
        return sum + (qty > 0 ? qty : 1) * unitWeight;
      }, 0);

      if (computedWeight > 0) {
        weight = computedWeight;
      }
    } catch {
      // Fall back to provided shipment-level values.
    }

    return {
      numberOfPieces: pieces,
      totalShipmentWeightLbs: Number.isFinite(weight) ? weight : 0,
    };
  })();

  const flat = flattenWorkflowTemplate(template).slice().sort((a, b) => a.day - b.day);

  const eventsOnSameDay = new Map<number, number>();
  const firstEventBase = roundUpToNearestMinutes(orderDate, 5);

  const scheduledEvents = flat.map((step, index) => {
    const eventDate = addDays(orderDate, step.day);
    const slot = phaseTimeSlot(step.phase);
    const sameDayIndex = eventsOnSameDay.get(step.day) || 0;
    eventsOnSameDay.set(step.day, sameDayIndex + 1);

    if (step.day === 0) {
      eventDate.setHours(
        firstEventBase.getHours(),
        firstEventBase.getMinutes(),
        0,
        0,
      );
      // If several events share day 0, space them in realistic short operational intervals.
      eventDate.setMinutes(eventDate.getMinutes() + sameDayIndex * 35);
    } else {
      eventDate.setHours(slot.hour, slot.minute, 0, 0);
      // If several events share the same future day, preserve clear event sequencing.
      eventDate.setMinutes(eventDate.getMinutes() + sameDayIndex * 35);
    }

    return {
      id: `evt-${step.day}-${index + 1}`,
      dayOffset: step.day,
      label: step.status,
      description: step.info,
      location: phaseLocation(
        step.phase,
        storeAddress,
        shippingWarehouseAddress,
        secondaryWarehouseAddress,
        customerAddress,
      ),
      phase: step.phase,
      isDamageEvent: step.isDamageEvent,
      isReplacementEvent: step.isReplacementEvent,
      timestamp: formatDateTime(eventDate),
      eventDate,
    };
  });

  let activeIndex = -1;
  for (let i = scheduledEvents.length - 1; i >= 0; i -= 1) {
    if (today.getTime() >= scheduledEvents[i].eventDate.getTime()) {
      activeIndex = i;
      break;
    }
  }

  const events: TrackingEvent[] = scheduledEvents.map(({ eventDate, ...event }, index) => {
    let state: EventState = 'pending';

    if (activeIndex === -1) {
      state = 'pending';
    } else if (index < activeIndex) {
      state = 'completed';
    } else if (index === activeIndex) {
      if (event.phase === 'delivered') {
        state = 'completed';
      } else {
        state = event.isDamageEvent ? 'damage' : 'active';
      }
    }

    return {
      ...event,
      state,
    };
  });

  let storedTrackingEvents: Array<{ id:string; proof?:{filename:string; dataUrl:string; uploadedAt:string} }> = [];
  if (storedTrackingEventsJson) {
    try {
      storedTrackingEvents = JSON.parse(storedTrackingEventsJson);
    } catch {
      storedTrackingEvents = [];
    }
  }

  const eventsWithAttachment = events.map((evt) => {
    const stored = storedTrackingEvents.find((x) => x.id === evt.id);
    return stored ? { ...evt, proof: stored.proof } : evt;
  });

  const currentEvent = activeIndex >= 0 ? eventsWithAttachment[activeIndex] : eventsWithAttachment[0];
  const maxDay = flat.reduce((max, x) => Math.max(max, x.day), 0);
  const isDelivered = currentEvent.phase === 'delivered';
  const isDamaged = eventsWithAttachment.some((x) => x.isDamageEvent && (x.state === 'active' || x.state === 'completed' || x.state === 'damage'));
  const hasReplacement = eventsWithAttachment.some((x) => x.isReplacementEvent && (x.state === 'active' || x.state === 'completed' || x.state === 'damage'));

  const progressPercent = Math.min(
    100,
    Math.round(((activeIndex + 1) / eventsWithAttachment.length) * 100)
  );

  const estimatedDeliveryDate = addDays(orderDate, maxDay);
  const standardDeliveryDate = addDays(orderDate, 21);
  const damagedDeliveryDate = addDays(standardDeliveryDate, 7);
  const replacementWindowStart = addDays(today, 7);
  const replacementWindowEnd = addDays(today, 21);

  const { estimatedDelivery, estimatedDeliveryNote } = (() => {
    if (isDelivered) {
      const deliveredEvent = [...eventsWithAttachment]
        .reverse()
        .find((event) => event.phase === 'delivered' && event.state === 'completed');

      return {
        estimatedDelivery: deliveredEvent?.timestamp
          ? formatDate(parseDateInput(deliveredEvent.timestamp))
          : formatDate(estimatedDeliveryDate),
        estimatedDeliveryNote: 'Shipment delivered successfully.',
      };
    }

    if (currentEvent.phase === 'replacement_review' || currentEvent.phase === 'replacement_transit') {
      return {
        estimatedDelivery: formatDateRange(replacementWindowStart, replacementWindowEnd),
        estimatedDeliveryNote:
          'Your replacement shipment is now in progress. Delivery is usually expected within 7 to 21 days from this stage.',
      };
    }

    if (currentEvent.phase === 'damaged' || currentEvent.phase === 'return') {
      return {
        estimatedDelivery: formatDate(damagedDeliveryDate),
        estimatedDeliveryNote:
          'A damage issue was reported. Please allow up to 7 additional days while the shipment is being returned and reviewed.',
      };
    }

    return {
      estimatedDelivery: formatDate(standardDeliveryDate),
      estimatedDeliveryNote:
        'Standard delivery estimate is within 21 days from order confirmation, and your shipment may arrive earlier depending on routing and handling.',
    };
  })();

  return {
    trackingNumber,
    orderNumber,
    storeName,
    storeAddress,
    shippingWarehouseAddress,
    secondaryWarehouseAddress,
    customerName,
    customerAddress,
    orderDate: formatDate(orderDate),
    numberOfPieces,
    totalShipmentWeightLbs,
    currentStatus: currentEvent.label,
    currentPhase: currentEvent.phase,
    progressPercent,
    events: eventsWithAttachment,
    estimatedDelivery,
    estimatedDeliveryNote,
    isDelivered,
    isDamaged,
    hasReplacement,
  };
}

export function buildTrackingRouteStops(result: TrackingResult): TrackingRouteStop[] {
  const compact = (value: string) => value.trim();

  // Route map should always represent truck delivery as store -> customer.
  const stopCandidates = [
    { id: 'store', label: 'Store Address', address: compact(result.storeAddress) },
    { id: 'customer', label: 'Customer Address', address: compact(result.customerAddress) },
  ].filter((stop) => Boolean(stop.address));

  // Remove duplicated addresses while preserving route order.
  const dedupedStops: Array<{ id: string; label: string; address: string }> = [];
  const seenAddress = new Set<string>();
  for (const stop of stopCandidates) {
    const key = stop.address.toLowerCase();
    if (seenAddress.has(key)) continue;
    seenAddress.add(key);
    dedupedStops.push(stop);
  }

  const currentIndex = (() => {
    switch (result.currentPhase) {
      case 'initial':
        return 0;
      case 'delivered':
        return Math.max(dedupedStops.length - 1, 0);
      default:
        return Math.min(1, Math.max(dedupedStops.length - 1, 0));
    }
  })();

  return dedupedStops.map((stop, index) => ({
    ...stop,
    status: index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'upcoming',
  }));
}

export function generateTrackingNumber(): string {
  let value = '';
  for (let i = 0; i < 12; i += 1) {
    value += Math.floor(Math.random() * 10).toString();
  }
  return value;
}

export type { TrackingEvent as TrackingEventType };